const MARKETING_PIXEL_REFERENCE_ERROR =
  /^(?:(?:referenceerror:\s*)?(?:fbq|rdt) is not defined|can't find variable:\s*(?:fbq|rdt))$/i;

const STALE_CHUNK_LOAD_FAILURE =
  /^(?:importing a module script failed\.?|failed to fetch dynamically imported module\b|error loading dynamically imported module\b|failed to load module script\b|unable to preload css\b)/i;

// Safari/WebKit TypeError for failed `fetch()` *and* some module-script misses.
// Exact-match only: a prefix would swallow "Load failed to …" product copy, and
// it must not drive a page reload (Chrome's equivalent `Failed to fetch` does
// not). Real chunk 404s still recover via the patterns above, Vite preload
// errors, and hashed `/next/assets/*` listeners.
const SAFARI_GENERIC_LOAD_FAILED = /^load failed\.?$/i;

const CROSS_ORIGIN_SCRIPT_ERROR = /^script error\.?$/i;

/**
 * Post-deploy / stale-tab hashed asset failures: dynamic `import()`, Vite
 * modulepreload, and WebKit module-script 404s. Shared by the one-shot page
 * reload; PostHog noise filtering uses this plus a few non-reload messages.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (isNamedChunkLoadError(error)) return true;
  return extractExceptionMessages(error).some((message) =>
    isChunkLoadErrorMessage(message),
  );
}

export function isNoisyClientException(error: unknown): boolean {
  if (isChunkLoadError(error)) return true;
  return extractExceptionMessages(error).some((message) =>
    isNoisyClientExceptionMessage(message),
  );
}

export function shouldDropPostHogExceptionEvent(event: unknown): boolean {
  if (!isRecord(event) || event.event !== '$exception') return false;
  return isNoisyClientException(event.properties);
}

function isNamedChunkLoadError(error: unknown): boolean {
  return error instanceof Error && error.name === 'ChunkLoadError';
}

function isChunkLoadErrorMessage(message: string): boolean {
  const normalized = normalizeExceptionMessage(message);
  return normalized.length > 0 && STALE_CHUNK_LOAD_FAILURE.test(normalized);
}

function isNoisyClientExceptionMessage(message: string): boolean {
  const normalized = normalizeExceptionMessage(message);
  if (normalized.length === 0) return false;
  return (
    MARKETING_PIXEL_REFERENCE_ERROR.test(normalized) ||
    SAFARI_GENERIC_LOAD_FAILED.test(normalized) ||
    CROSS_ORIGIN_SCRIPT_ERROR.test(normalized)
  );
}

function normalizeExceptionMessage(message: string): string {
  return message.trim().replaceAll(/[\u2018\u2019]/g, "'");
}

function extractExceptionMessages(error: unknown): string[] {
  if (typeof error === 'string') return [error];
  if (error instanceof Error) return [error.message];
  if (!isRecord(error)) return [];

  const messages: string[] = [];
  if (typeof error.message === 'string') messages.push(error.message);
  if (typeof error.value === 'string') messages.push(error.value);

  const list = error.$exception_list;
  if (Array.isArray(list)) {
    for (const item of list) {
      if (isRecord(item) && typeof item.value === 'string') {
        messages.push(item.value);
      }
    }
  }

  const values = error.$exception_values;
  if (Array.isArray(values)) {
    for (const value of values) {
      if (typeof value === 'string') messages.push(value);
    }
  }

  return messages;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
